import { useAppSelector } from '@/store/store-hooks'
import { getAuth } from 'firebase/auth'
import { constructArtifactFilePath } from '@/artifacts/util/construct-artifact-file-path'
import { kGcsArtifactsBucketRef } from '@/constants/constants-gcs'
import ArtifactEditor from '@/artifact-editor/components/ArtifactEditor'
import ArtifactReadOnlyViewer from '@/content-viewer/components/artifact/ArtifactReadOnlyViewer'
import { artifactNameFallback } from '@/constants/constants-artifact'
import ContentViewerHeader from '@/content-viewer/components/common/ContentViewerHeader'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import { CaseStatusInfoTag } from '@/citator/components/CaseStatusInfoTag'
import { isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import { CitatorSurface } from '@/analytics/enums/citator.enums'
import PDFBaseRenderer from './base-renderers/PDFBaseRenderer'
import MarkdownRenderer from './file/MarkdownRenderer'
import { RenderFormat, ViewerDocumentKind, ViewerOpenContextKind, type ContentViewerSession } from '../store/schemas'
import { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import UserFileContentRenderer from '@/user_files/files/components/UserFileContentRenderer'
import CaseLawRenderer from './agent-reference/renderers/CaseLawRenderer'
import LegalResearchReportRenderer from './agent-reference/renderers/LegalResearchReportRenderer'
import { useFootnoteInfoByCitationId } from '@/agent/references/hooks/useFootnoteInfoByCitationId'
import { createRiaContext } from '@/analytics/utils/ria-source-analytics'
import { selectAgentConversationFromId, selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { selectActiveContentViewerSession } from '../store/selectors'
import WritingSamplePdfRenderer from './agent-reference/renderers/WritingSamplePdfRenderer'
import MedSummaryConvertedPdfRenderer from '@/medical-billing-summaries/sources/MedSummaryConvertedPdfRenderer'

// Stable empty array references to prevent memoization breaks
const EMPTY_PDF_HIGHLIGHTS: PaxtonHighlightArea[] = []
const EMPTY_MARKDOWN_HIGHLIGHTS: string[] = []

type ContentViewerConstructorProps = {
  conversationId: string
}
/**
 * Content Viewer Constructor
 *
 * Routes normalized viewer sessions to the correct layout and renderer.
 */
export default function ContentViewerConstructor(props: ContentViewerConstructorProps) {
  const { conversationId } = props

  // Agent conversation content viewer containing an artifact or reference
  const contentViewerSession = useAppSelector(selectActiveContentViewerSession)
  const citationContext = contentViewerSession?.context.kind === ViewerOpenContextKind.CHAT_CITATION ? contentViewerSession.context : null
  const footnoteInfoByCitationId = useFootnoteInfoByCitationId(citationContext?.turnId ?? null)
  const sessionMatterId = useAppSelector((state) =>
    citationContext ? selectAgentConversationMatterId(state, { conversationId: citationContext.conversationId }) : null
  )
  const artifactConversationId = getArtifactSourceConversationId(contentViewerSession, conversationId)
  const artifactConversation = useAppSelector((state) => selectAgentConversationFromId(state, { conversationId: artifactConversationId }))
  const artifactMatterId = artifactConversation?.matter_id ?? null
  const currentUserId = getAuth().currentUser?.uid
  // Read-only vs editor is decided by the artifact's source conversation, which is
  // always in the store here because artifacts are only opened from within their
  // conversation in the assistant. If artifacts ever open from another surface,
  // revisit this: an unknown owner falls back to the editor (own-Firebase) path.
  const useReadOnlyArtifactViewer = Boolean(artifactConversation?.user_id && currentUserId && artifactConversation.user_id !== currentUserId)

  if (!contentViewerSession) {
    return null
  }

  return renderSessionContent({
    session: contentViewerSession,
    conversationId,
    footnoteNumber: citationContext ? footnoteInfoByCitationId[citationContext.citationId]?.footnoteNumber : undefined,
    matterId: sessionMatterId,
    artifactMatterId,
    artifactSourceConversationId: artifactConversationId ?? conversationId,
    useReadOnlyArtifactViewer,
  })
}

function renderSessionContent(args: {
  session: ContentViewerSession
  conversationId: string
  footnoteNumber?: number
  matterId: string | null
  artifactMatterId: string | null
  artifactSourceConversationId: string
  useReadOnlyArtifactViewer: boolean
}) {
  const { session, conversationId, footnoteNumber, matterId, artifactMatterId, artifactSourceConversationId, useReadOnlyArtifactViewer } = args
  const { document, presentation } = session

  switch (document.kind) {
    case ViewerDocumentKind.ARTIFACT: {
      const title = document.name || artifactNameFallback

      if (useReadOnlyArtifactViewer) {
        return (
          <ArtifactReadOnlyViewer
            conversationId={conversationId}
            sourceConversationId={artifactSourceConversationId}
            matterId={artifactMatterId}
            artifactVersionId={document.artifactVersionId}
            title={title}
            highlightSentences={presentation?.markdownHighlights ?? EMPTY_MARKDOWN_HIGHLIGHTS}
          />
        )
      }

      const filePath = constructArtifactFilePath(document.artifactVersionId)

      return (
        <ArtifactEditor
          conversationId={conversationId}
          filePath={filePath}
          title={title}
          gcsBucketName={kGcsArtifactsBucketRef}
          artifactVersionId={document.artifactVersionId}
        />
      )
    }

    case ViewerDocumentKind.LEGACY_FILE: {
      const { renderAsset } = document
      const title = getHeaderTitle(session, document.title, footnoteNumber)

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader conversationId={conversationId} loading={false} title={title} />
          {renderAsset.format === RenderFormat.PDF ? (
            <PDFBaseRenderer
              filePath={renderAsset.filePath}
              gcsBucketRef={renderAsset.gcsBucketRef}
              legacy_always_cloud={renderAsset.legacyAlwaysCloud}
              highlights={presentation?.pdfHighlights ?? EMPTY_PDF_HIGHLIGHTS}
              startingPage={presentation?.startingPage}
              title={title}
            />
          ) : (
            <div className="flex-1 w-full h-full overflow-y-auto pb-4">
              <MarkdownRenderer
                filePath={renderAsset.filePath}
                gcsBucketName={renderAsset.gcsBucketRef}
                legacy_always_cloud={renderAsset.legacyAlwaysCloud}
                highlightSentences={presentation?.markdownHighlights ?? EMPTY_MARKDOWN_HIGHLIGHTS}
              />
            </div>
          )}
        </div>
      )
    }

    case ViewerDocumentKind.USER_FILE: {
      const { renderAsset } = document
      const title = getHeaderTitle(session, document.title, footnoteNumber)
      const renderer = (
        <UserFileContentRenderer
          userFileId={document.userFileId}
          format={renderAsset.format}
          pdfHighlights={presentation?.pdfHighlights ?? EMPTY_PDF_HIGHLIGHTS}
          markdownHighlights={presentation?.markdownHighlights ?? EMPTY_MARKDOWN_HIGHLIGHTS}
          startingPage={presentation?.startingPage}
          title={title}
        />
      )

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader conversationId={conversationId} loading={false} title={title} />
          {renderAsset.format === RenderFormat.PDF ? renderer : <div className="flex-1 w-full h-full overflow-y-auto pb-4">{renderer}</div>}
        </div>
      )
    }

    case ViewerDocumentKind.CASELAW: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)
      const caseStatus = document.caseStatus
      const badge = isDisplayableCaseStatus(caseStatus) ? (
        <CaseStatusInfoTag status={caseStatus} checkedLabelSize="default" surface={CitatorSurface.Chat} trackImpression={false} />
      ) : undefined
      const riaContext =
        session.context.kind === ViewerOpenContextKind.CHAT_CITATION ? createRiaContext(session.context.conversationId, session.context.turnId, matterId) : null

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader conversationId={conversationId} loading={false} title={title} badge={badge} />
          <CaseLawRenderer
            s3_path={document.s3Path}
            highlightSentences={presentation?.markdownHighlights}
            parentId={document.parentId}
            riaContext={riaContext}
            defaultTab={presentation?.defaultTab}
            resetKey={session.context.kind === ViewerOpenContextKind.CHAT_CITATION ? session.context.agentReferenceId : undefined}
          />
        </div>
      )
    }

    case ViewerDocumentKind.LRR: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader conversationId={conversationId} loading={false} title={title} />
          <LegalResearchReportRenderer s3_path={document.s3Path} highlightSentences={presentation?.markdownHighlights} />
        </div>
      )
    }

    case ViewerDocumentKind.WRITING_SAMPLE: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader conversationId={conversationId} loading={false} title={title} />
          {matterId ? (
            <WritingSamplePdfRenderer referenceId={document.referenceId} matterId={matterId} title={document.title} startingPage={presentation?.startingPage} />
          ) : (
            <ContentViewerErrorState message="Could not load writing sample." />
          )}
        </div>
      )
    }

    case ViewerDocumentKind.MED_SUMMARY_CONVERTED_PDF: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader conversationId={conversationId} loading={false} title={title} />
          <MedSummaryConvertedPdfRenderer
            assetId={document.assetId}
            userFileId={document.userFileId}
            title={document.title}
            pdfHighlights={presentation?.pdfHighlights ?? EMPTY_PDF_HIGHLIGHTS}
            startingPage={presentation?.startingPage}
          />
        </div>
      )
    }
  }
}

function getHeaderTitle(session: ContentViewerSession, title: string, footnoteNumber?: number) {
  if (session.context.kind !== ViewerOpenContextKind.CHAT_CITATION || !footnoteNumber) return title
  return `[${footnoteNumber}] ${title}`
}

function getArtifactSourceConversationId(session: ContentViewerSession | null, fallbackConversationId: string): string | undefined {
  if (session?.document.kind !== ViewerDocumentKind.ARTIFACT) return undefined
  if (session.context.kind === ViewerOpenContextKind.CHAT_ARTIFACT || session.context.kind === ViewerOpenContextKind.AUTO_OPEN) {
    return session.context.conversationId ?? fallbackConversationId
  }
  return fallbackConversationId
}
